import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getMode, setMode as persistMode, applyMode, type Mode } from "../lib/mode";
import { ensureSandbox } from "../lib/sandbox";

// Test/Live sandbox binding for React. Reading `mode` lets components (banner, toggle, gated UI)
// react to the current mode; switching persists it and does a full reload so EVERY data-loading
// view refetches under the new `x-rt-mode` header — the same clean-slate approach Stripe uses,
// avoiding stale live/test data lingering across dozens of independently-fetching screens.

interface ModeState {
  mode: Mode;
  isTest: boolean;
  busy: boolean;
  /** Persist the new mode and reload so all data refetches under it. Entering test mode first
   *  provisions the user's sandbox (idempotent). No-op if unchanged. */
  switchMode: (m: Mode) => Promise<void>;
}

const ModeContext = createContext<ModeState | null>(null);

export function useMode(): ModeState {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be used within <ModeProvider>");
  return ctx;
}

export function ModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() => getMode());
  const [busy, setBusy] = useState(false);

  // Keep <html data-mode> in sync (banner + chrome style off it), incl. on first load.
  useEffect(() => {
    applyMode(mode);
  }, [mode]);

  async function switchMode(m: Mode): Promise<void> {
    if (m === mode || busy) return;
    setBusy(true);
    // Provision the sandbox before entering test mode so the user lands on populated data.
    // Best-effort: if it fails, still switch (they'll just see the shared base until it retries).
    if (m === "test") {
      try {
        await ensureSandbox();
      } catch {
        /* ignore — the base dataset is still visible; ensure retries on next entry */
      }
    }
    persistMode(m);
    setModeState(m);
    window.location.reload();
  }

  return (
    <ModeContext.Provider value={{ mode, isTest: mode === "test", busy, switchMode }}>
      {children}
    </ModeContext.Provider>
  );
}
